import { apiService } from "./api";
import { API_CONFIG, buildUrl } from "../config/api";
import {
    Order,
    CreateOrderRequest,
    PaginationParams,
    OrderSummary,
    OrderSearchParams,
    OrderItem,
    DashboardSummary,
    DashboardAnalyticsParams,
    OrderStatisticsResponse,
    PaginatedStatsResponse,
    PaymentTransaction,
    PaymentMethod,
    PaymentStatus,
    PaymentSummary,
} from "../types";

export interface AddPaymentRequest {
    amount: number;
    payment_method: PaymentMethod;
    reference_number?: string;
    notes?: string;
    account_id?: string | null;
}

export interface BkashRefundResult {
    transaction_id: string;
    payment_status: PaymentStatus;
    amount_paid: number;
    amount_due: number;
    message: string;
    // "still processing" — bKash's own result was inconclusive even after
    // a Refund Status follow-up. Not a failure — no ledger row was
    // created; use getBkashRefundStatus once bKash confirms it.
    pending: boolean;
}

export interface BkashRefundStatusResult {
    transaction_id: string;
    payment_status: PaymentStatus;
    amount_paid: number;
    amount_due: number;
    newly_recorded: number;
    message: string;
}

// A payment plus enough of its parent order to display in a cross-order
// list (e.g. "all payments received today"), without a second lookup per
// row — mirrors PurchasePaymentWithPurchase in procurementService.ts.
export interface OrderPaymentWithOrder extends PaymentTransaction {
    order_number: string;
    order_status: Order["status"];
    customer_name?: string;
    delivery_service_name?: string;
    delivery_service_type?: "INHOUSE" | "THIRD_PARTY";
    order_channel_id?: string;
    order_channel_type?: "OFFLINE" | "ONLINE";
    delivery_cost: number;
}

export class OrderService {
    // Orders
    async getOrders(
        params?: PaginationParams & {
            status?: Order["status"];
            customer_id?: string;
            days?: number;
        },
    ): Promise<{ items: Order[]; total: number; skip: number; limit: number }> {
        const queryParams = new URLSearchParams();
        if (params?.skip !== undefined)
            queryParams.append("skip", params.skip.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());
        if (params?.status) queryParams.append("status", params.status);
        if (params?.customer_id)
            queryParams.append("customer_id", params.customer_id);
        if (params?.days) queryParams.append("days", params.days.toString());

        const url = `${
            API_CONFIG.ENDPOINTS.ORDERS.BASE
        }?${queryParams.toString()}`;
        return apiService.get<{
            items: Order[];
            total: number;
            skip: number;
            limit: number;
        }>(url);
    }

    async getOrderById(id: string): Promise<Order> {
        return apiService.get<Order>(
            `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/${id}`,
        );
    }

    async getOrderForReceipt(id: string): Promise<Order> {
        return apiService.get<Order>(
            `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/${id}/receipt`,
        );
    }

    async createOrder(data: CreateOrderRequest): Promise<Order> {
        return apiService.post<Order>(API_CONFIG.ENDPOINTS.ORDERS.BASE, data);
    }

    /**
     * Partial update. `customer_address_id` (not an Order field) marks the
     * shipping address as picked from that saved address, which the server
     * then makes the customer's default.
     */
    async updateOrder(
        id: string,
        data: Partial<Order> & { customer_address_id?: string; delivery_area_id?: string | null },
    ): Promise<Order> {
        return apiService.put<Order>(
            `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/${id}`,
            data,
        );
    }

    /** Delivery charge / delivery cost only. The server re-derives the
     * total and payment status, and returns the full order. */
    async updateOrderCharges(
        id: string,
        data: { shipping_amount?: number; delivery_cost?: number; discount_amount?: number },
    ): Promise<Order> {
        return apiService.patch<Order>(
            `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/${id}/charges`,
            data,
        );
    }

    async updateOrderStatus(id: string, status: string): Promise<any> {
        return apiService.patch(
            `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/${id}/status`,
            { status },
        );
    }

    async cancelOrder(id: string, reason: string): Promise<Order> {
        const url = buildUrl(API_CONFIG.ENDPOINTS.ORDERS.CANCEL, { id });
        return apiService.put<Order>(url, { reason });
    }

    // Hard delete — the backend only allows this for DRAFT orders (see
    // OrderService.delete); anything else must be cancelled instead.
    async deleteOrder(id: string): Promise<void> {
        return apiService.delete<void>(
            `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/${id}`,
        );
    }

    async getOrdersByCustomer(
        customerId: string,
        params?: PaginationParams & {
            status?: Order["status"];
        },
    ): Promise<Order[]> {
        const queryParams = new URLSearchParams();
        if (params?.skip) queryParams.append("skip", params.skip.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());
        if (params?.status) queryParams.append("status", params.status);

        const url = `${
            API_CONFIG.ENDPOINTS.ORDERS.BY_CUSTOMER
        }/${customerId}?${queryParams.toString()}`;
        return apiService.get<Order[]>(url);
    }

    async getOrdersByStatus(
        status: Order["status"],
        params?: PaginationParams,
    ): Promise<Order[]> {
        const queryParams = new URLSearchParams();
        if (params?.skip) queryParams.append("skip", params.skip.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());

        const url = `${
            API_CONFIG.ENDPOINTS.ORDERS.BY_STATUS
        }/${status}?${queryParams.toString()}`;
        return apiService.get<Order[]>(url);
    }

    async getRecentOrders(
        params?: PaginationParams & { days?: number },
    ): Promise<Order[]> {
        const queryParams = new URLSearchParams();
        if (params?.days) queryParams.append("days", params.days.toString());
        if (params?.skip) queryParams.append("skip", params.skip.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());

        const url = `${
            API_CONFIG.ENDPOINTS.ORDERS.RECENT
        }?${queryParams.toString()}`;
        return apiService.get<Order[]>(url);
    }

    async searchOrders(
        params: OrderSearchParams,
    ): Promise<{ items: Order[]; total: number; skip: number; limit: number }> {
        const url = `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/search`;
        return apiService.post<{
            items: Order[];
            total: number;
            skip: number;
            limit: number;
        }>(url, params);
    }

    // All order payments whose payment_date falls within the given range,
    // across every order — e.g. "what cash did we receive today",
    // regardless of whether the order itself was placed/delivered today
    // or days ago. dateFromIso/dateToIso should carry a timezone offset.
    async searchOrderPayments(
        dateFromIso: string,
        dateToIso: string,
    ): Promise<OrderPaymentWithOrder[]> {
        return apiService.get<OrderPaymentWithOrder[]>(
            "/orders/payments/search",
            { params: { date_from: dateFromIso, date_to: dateToIso } },
        );
    }

    async exportOrders(params: OrderSearchParams): Promise<Blob> {
        const url = `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/export`;
        return apiService.post<Blob>(url, params, { responseType: "blob" });
    }

    async getOrderStatistics(
        params: OrderSearchParams,
    ): Promise<OrderStatisticsResponse> {
        const url = `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/search/statistics`;
        const response = await apiService.post<OrderStatisticsResponse>(
            url,
            params,
        );
        return response;
    }

    async getTopProductsStats(
        params: OrderSearchParams,
        skip: number = 0,
        limit: number = 10,
    ): Promise<PaginatedStatsResponse> {
        const url = `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/search/statistics/products?skip=${skip}&limit=${limit}`;
        const response = await apiService.post<PaginatedStatsResponse>(
            url,
            params,
        );
        return response;
    }

    async getTopProductsByAmountStats(
        params: OrderSearchParams,
        skip: number = 0,
        limit: number = 10,
    ): Promise<PaginatedStatsResponse> {
        const url = `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/search/statistics/products/by-amount?skip=${skip}&limit=${limit}`;
        const response = await apiService.post<PaginatedStatsResponse>(
            url,
            params,
        );
        return response;
    }

    async getTopCustomersStats(
        params: OrderSearchParams,
        skip: number = 0,
        limit: number = 10,
    ): Promise<PaginatedStatsResponse> {
        const url = `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/search/statistics/customers?skip=${skip}&limit=${limit}`;
        const response = await apiService.post<PaginatedStatsResponse>(
            url,
            params,
        );
        return response;
    }

    // Order Items
    async getOrderItems(orderId: string): Promise<OrderItem[]> {
        const url = buildUrl(API_CONFIG.ENDPOINTS.ORDERS.ITEMS, {
            id: orderId,
        });
        return apiService.get<OrderItem[]>(url);
    }

    async addOrderItem(
        orderId: string,
        itemData: {
            product_id: string;
            quantity: number;
            unit_price: number;
            total_price: number;
            is_gift?: boolean;
            allow_negative_stock?: boolean;
        },
    ): Promise<OrderItem> {
        return apiService.post<OrderItem>(
            `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/${orderId}/items`,
            itemData,
        );
    }

    async updateOrderItem(
        itemId: string,
        data: {
            quantity?: number;
            unit_price?: number;
            allow_negative_stock?: boolean;
        },
    ): Promise<OrderItem> {
        return apiService.put<OrderItem>(
            `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/items/${itemId}`,
            data,
        );
    }

    async deleteOrderItem(itemId: string): Promise<void> {
        return apiService.delete<void>(
            `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/items/${itemId}`,
        );
    }

    async removeOrderItem(itemId: string): Promise<void> {
        // Deprecated: use deleteOrderItem instead
        return this.deleteOrderItem(itemId);
    }

    // Payment Transactions
    async addPayment(
        orderId: string,
        data: AddPaymentRequest,
    ): Promise<PaymentTransaction> {
        return apiService.post<PaymentTransaction>(
            `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/${orderId}/payments`,
            data,
        );
    }

    async deletePayment(transactionId: string): Promise<void> {
        return apiService.delete<void>(`/payments/${transactionId}`);
    }

    async refundBkashPayment(
        orderId: string,
        transactionId: string,
        data: { refund_amount: number; reason: string; sku?: string },
    ): Promise<BkashRefundResult> {
        return apiService.post<BkashRefundResult>(
            `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/${orderId}/payments/${transactionId}/bkash-refund`,
            data,
        );
    }

    // Authoritative payment_status/amount_paid/amount_due plus the full
    // transaction list, in one call — used to refresh state after a
    // refund rather than recomputing it client-side (see REFUNDED status,
    // which the drawer's own add/delete-payment ternaries don't know
    // about).
    async getPaymentSummary(orderId: string): Promise<PaymentSummary> {
        return apiService.get<PaymentSummary>(
            `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/${orderId}/payment-summary`,
        );
    }

    async getBkashRefundStatus(
        orderId: string,
        transactionId: string,
    ): Promise<BkashRefundStatusResult> {
        return apiService.post<BkashRefundStatusResult>(
            `${API_CONFIG.ENDPOINTS.ORDERS.BASE}/${orderId}/payments/${transactionId}/bkash-refund-status`,
            {},
        );
    }

    // Order Statistics
    async getOrderSummary(params?: {
        start_date?: string;
        end_date?: string;
    }): Promise<OrderSummary> {
        try {
            const queryParams = new URLSearchParams();
            if (params?.start_date)
                queryParams.append("start_date", params.start_date);
            if (params?.end_date)
                queryParams.append("end_date", params.end_date);

            const url = `${
                API_CONFIG.ENDPOINTS.ORDERS.SUMMARY
            }?${queryParams.toString()}`;
            const response = await apiService.get<OrderSummary>(url);

            // Log the response for debugging
            console.log("Order summary response:", response);

            // Return the response with default values for any missing fields
            return {
                total_orders: response?.total_orders || 0,
                total_revenue: response?.total_revenue || 0,
                average_order_value: response?.average_order_value || 0,
                orders_by_status: response?.orders_by_status || {
                    DRAFT: 0,
                    PENDING: 0,
                    CONFIRMED: 0,
                    SHIPPED: 0,
                    DELIVERED: 0,
                    CANCELLED: 0,
                },
                top_products: response?.top_products || [],
            };
        } catch (error) {
            console.error("Error fetching order summary:", error);
            // Return default values in case of error
            return {
                total_orders: 0,
                total_revenue: 0,
                average_order_value: 0,
                orders_by_status: {
                    DRAFT: 0,
                    PENDING: 0,
                    CONFIRMED: 0,
                    SHIPPED: 0,
                    DELIVERED: 0,
                    CANCELLED: 0,
                },
                top_products: [],
            };
        }
    }

    // Dashboard Analytics
    async getDashboardAnalytics(
        params: DashboardAnalyticsParams = {},
    ): Promise<DashboardSummary> {
        const queryParams = new URLSearchParams();

        console.log("[Analytics] Request params:", params);

        // Handle preset parameter
        if (params.preset === "all_time" || params.preset === "custom") {
            // For all_time or custom, don't send the preset parameter
            // For custom, start_date and end_date will be used
            console.log(
                "[Analytics] Special preset selected (all_time/custom) - relying on specific handling",
            );
        } else if (params.preset) {
            // Send specific preset
            console.log("[Analytics] Adding preset:", params.preset);
            queryParams.append("preset", params.preset);
        } else if (!params.start_date && !params.end_date) {
            // No preset and no custom dates - default to this_month
            console.log(
                "[Analytics] No params provided, defaulting to this_month",
            );
            queryParams.append("preset", "this_month");
        }

        if (params.start_date)
            queryParams.append("start_date", params.start_date);
        if (params.end_date) queryParams.append("end_date", params.end_date);
        if (params.include_comparison)
            queryParams.append("include_comparison", "true");
        if (params.include_daily_breakdown)
            queryParams.append("include_daily_breakdown", "true");

        const url = `${
            API_CONFIG.ENDPOINTS.ORDERS.BASE
        }/analytics/dashboard?${queryParams.toString()}`;
        console.log("[Analytics] API URL:", url);

        const response = await apiService.get<DashboardSummary>(url);
        console.log("[Analytics] Response:", {
            period: response.period_label,
            total_orders: response.total_orders,
            revenue: response.total_revenue,
        });

        return response;
    }
}

export const orderService = new OrderService();
